import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
// Paper comes from v4 while everything else here is v5. That is not the usual
// v4-lab + v5-material mix (see common/FormAutocomplete) — it is required: Paper is
// rendered as the v4 Autocomplete's PaperComponent, so it receives that popup's own
// v4 JSS className. A v5 Paper would style itself from the v5 theme/emotion while
// wearing a v4 class, which is the one place the two systems actually meet.
import Paper from '@material-ui/core/Paper';
import { CircularProgress, TextField, Typography } from '@mui/material';
import { useController } from 'react-hook-form';

import { useSearchSupplierMutation } from '../../services/servicePostAJob';
import { TOKENS } from '../../styles/tokens';
import {
  DEFAULT_COMPANY_HELPER,
  ICompanyAutocompleteProps,
  ICompanyOption,
  LINKED_COMPANY_HELPER,
  MIN_SEARCH_LEN,
  SEARCH_DEBOUNCE_MS
} from './interfaces';
import { useStyles } from './styles';

// Local clone of common's formatterLabel (common is frozen) — label + red required asterisk.
const formatterLabel = (label?: string, isRequired?: boolean) => {
  if (!label) {
    return null;
  }
  const cap = label.charAt(0).toUpperCase() + label.slice(1);
  if (!isRequired) {
    return cap;
  }
  return (
    <>
      {cap}
      {' '}
      <span style={{ color: 'red' }}> *</span>
    </>
  );
};

// Up to two-letter initials for the logo-fallback avatar.
const getInitials = (text: string): string => (text || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w.charAt(0))
  .join('')
  .toUpperCase();

export const CompanyAutocomplete = memo(({
  extraProps,
  supplierIdName,
  supplierTitleName,
  setValue,
  getValues,
  errors = false,
  defaultSupplier,
  label = 'Company Name',
  isRequired = true,
  maxLength = 50,
  fieldComment = DEFAULT_COMPANY_HELPER,
  keepLinkOnEdit = false
}: ICompanyAutocompleteProps) => {
  const classes = useStyles();
  const { control, name } = extraProps;

  // (a) visible text controller — drives inputValue + text writes.
  const { field: companyField } = useController({ control, name });
  // (b),(c) hidden link-field controllers — keep both in registered form state so they
  // survive handleSubmit / useWatch(showChangedFields) / goBack / SaveJobData.
  const { field: supplierIdField } = useController({ control, name: supplierIdName });
  const { field: supplierTitleField } = useController({ control, name: supplierTitleName });

  const [searchSupplier, { data: suppliersData, isLoading }] = useSearchSupplierMutation();

  const [options, setOptions] = useState<ICompanyOption[]>([]);
  const [searched, setSearched] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const seededIdRef = useRef<string | null>(null);

  const companyValue: string = (companyField.value as string) || '';
  const idValue = supplierIdField.value;
  const titleValue: string = (supplierTitleField.value as string) || '';
  const linked = idValue !== null && idValue !== undefined && idValue !== '';
  // Orphaned-supplier edge (id set, title null) → chip label falls back to the typed name.
  const chipLabel = titleValue || companyValue;

  // Mirror server results into local options so pick / unlink can clear the popup.
  useEffect(() => {
    setOptions((suppliersData as ICompanyOption[]) || []);
  }, [suppliersData]);

  // Seed LINKED state from defaultSupplier (server prefill on EditJob + step re-entry).
  // Runs once per distinct id; never clobbers a value the form already carries.
  useEffect(() => {
    const seedId = defaultSupplier && defaultSupplier.id;
    if (seedId === null || seedId === undefined || (seedId as unknown as string) === '') {
      return;
    }
    const key = String(seedId);
    const currentId = getValues(supplierIdName);
    if (seededIdRef.current !== key && String(currentId === null || currentId === undefined ? '' : currentId) !== key) {
      setValue(supplierIdName, Number(seedId), { shouldDirty: false, shouldTouch: false });
      setValue(supplierTitleName, (defaultSupplier && defaultSupplier.title) || null, { shouldDirty: false, shouldTouch: false });
    }
    seededIdRef.current = key;
  }, [defaultSupplier, getValues, setValue, supplierIdName, supplierTitleName]);

  const runSearch = (raw: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const trimmed = raw.trim();
      if (trimmed.length >= MIN_SEARCH_LEN) {
        setSearched(true);
        searchSupplier({ search: trimmed });
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const unlink = useCallback(() => {
    setValue(supplierIdName, null, { shouldDirty: true, shouldTouch: true });
    setValue(supplierTitleName, null, { shouldDirty: true, shouldTouch: true });
  }, [setValue, supplierIdName, supplierTitleName]);

  // v4 lab fires 'reset' (with the raw option label) after a pick — accept only 'input'
  // so the ≤50 truncated title we wrote on pick sticks in the controlled field.
  const handleInputChange = (_e: React.ChangeEvent<{}>, value: string, reason: string) => {
    if (reason !== 'input') {
      return;
    }
    const capped = value.length > maxLength ? value.slice(0, maxLength) : value;
    companyField.onChange(capped);
    if (linked && !keepLinkOnEdit) {
      // Unlink synchronously with the text write so autosave / goBack snapshots are
      // never edited-text-but-still-linked.
      unlink();
    }
    runSearch(capped);
  };

  const handleChange = (_e: React.ChangeEvent<{}>, option: ICompanyOption | string | null, reason: string) => {
    if (reason === 'select-option' && option && typeof option === 'object') {
      if (option.keep) {
        // "Keep what I typed" — no directory link; leave the text as-is, drop the popup.
        unlink();
        setOptions([]);
        return;
      }
      const fullTitle = option.text || '';
      const truncated = fullTitle.length > maxLength ? fullTitle.slice(0, maxLength).trimEnd() : fullTitle;
      companyField.onChange(truncated);
      setValue(supplierIdName, Number(option.value), { shouldDirty: true, shouldTouch: true });
      setValue(supplierTitleName, fullTitle, { shouldDirty: true, shouldTouch: true });
      setOptions([]);
    }
  };

  const handleUnlinkClick = () => {
    unlink();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const hasError = !!(errors && errors[name]);
  const errorMessage = hasError && errors[name] ? errors[name].message : undefined;
  const trimmedValue = companyValue.trim();
  const showNoMatch = !linked
    && searched
    && !isLoading
    && !!suppliersData
    && suppliersData.length === 0
    && trimmedValue.length >= MIN_SEARCH_LEN;
  // Rich directory results + a trailing "keep what I typed" affordance.
  const matchCount = options.length;
  const showKeep = !linked && searched && trimmedValue.length >= MIN_SEARCH_LEN;
  const displayOptions: ICompanyOption[] = showKeep
    ? [...options, { keep: true, text: trimmedValue, value: '__keep__' }]
    : options;

  const renderHelper = () => {
    if (linked) {
      return (
        <>
          <div style={{ marginBottom: 8 }}>
            <span
              data-cy="companyLinkedChip"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '100%',
                padding: '5px 8px 5px 12px',
                borderRadius: 999,
                backgroundColor: TOKENS.goSoft,
                color: TOKENS.goDeep,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.35
              }}
            >
              <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                <span style={{ fontWeight: 700, marginRight: 6 }}>✓</span>
                {`Linked to ${chipLabel}`}
              </span>
              <button
                type="button"
                data-cy="companyUnlink"
                aria-label={`Linked to ${chipLabel}. Press Delete to unlink.`}
                onClick={handleUnlinkClick}
                style={{
                  flex: '0 0 auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 18,
                  height: 18,
                  marginLeft: 10,
                  padding: 0,
                  border: `1px solid ${TOKENS.lineDivider}`,
                  borderRadius: '50%',
                  background: 'transparent',
                  color: TOKENS.ink3,
                  fontSize: 13,
                  lineHeight: 1,
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </span>
          </div>
          <Typography
            className={classes.helperText}
            color="textSecondary"
            variant="body2"
          >
            {LINKED_COMPANY_HELPER}
          </Typography>
        </>
      );
    }
    if (isLoading) {
      return (
        <Typography
          className={classes.helperText}
          color="textSecondary"
          variant="body2"
        >
          Searching the Nombase directory…
        </Typography>
      );
    }
    if (showNoMatch) {
      return (
        <Typography
          className={classes.helperText}
          color="textSecondary"
          variant="body2"
        >
          No directory matches. You can keep the name you typed.
        </Typography>
      );
    }
    return (
      <Typography
        className={classes.helperText}
        color="textSecondary"
        variant="body2"
      >
        {fieldComment}
      </Typography>
    );
  };

  return (
    <div className={classes.wrapper}>
      <Autocomplete
        freeSolo
        fullWidth
        disableClearable
        clearOnBlur={false}
        options={displayOptions}
        filterOptions={(x) => x}
        value={null as any}
        inputValue={companyValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => (typeof option === 'string' ? option : (option && option.text) || '')}
        PaperComponent={(paperProps) => (
          <Paper
            {...paperProps}
            className={`${paperProps.className || ''} cpg-cmp-paper`}
          >
            {matchCount > 0 && (
              <div className="cpg-cmp-head">
                <span>
                  {matchCount}
                  {matchCount === 1 ? ' match' : ' matches'}
                  {' in the Nombase directory'}
                </span>
                <span className="cpg-cmp-head-hint">↑↓ navigate · ↵ select</span>
              </div>
            )}
            {paperProps.children}
          </Paper>
        )}
        renderOption={(option: ICompanyOption) => (option.keep ? (
          <div className="cpg-cmp-keep">
            <span
              className="cpg-cmp-keep-icon"
              aria-hidden="true"
            >
              ✎
            </span>
            <div className="cpg-cmp-row-main">
              <div className="cpg-cmp-keep-title">{`Keep “${option.text}” as written`}</div>
              <div className="cpg-cmp-row-sub">No directory link · use general terms for a confidential listing</div>
            </div>
          </div>
        ) : (
          <div className="cpg-cmp-row">
            <span className="cpg-cmp-avatar">
              {option.logo ? (
                <img
                  src={option.logo}
                  alt=""
                  className="cpg-cmp-logo"
                />
              ) : (
                <span className="cpg-cmp-initials">{getInitials(option.text)}</span>
              )}
            </span>
            <div className="cpg-cmp-row-main">
              <div className="cpg-cmp-row-name">{option.text}</div>
              {(option.category || option.location) ? (
                <div className="cpg-cmp-row-sub">
                  {[option.category, option.location].filter(Boolean).join(' · ')}
                </div>
              ) : null}
            </div>
            <span className="cpg-cmp-select">
              <span>Select</span>
              <span className="cpg-cmp-select-kbd">↵</span>
            </span>
          </div>
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={(el) => { inputRef.current = el; }}
            variant="outlined"
            label={formatterLabel(label, isRequired)}
            error={hasError}
            helperText={errorMessage}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress
                      color="inherit"
                      size={16}
                    />
                  ) : null}
                  <span className="cpg-cmp-tag">Powered by Nombase</span>
                  {params.InputProps.endAdornment}
                </>
              ),
              // native input attrs nested under InputProps (mirrors common's
              // TextFieldVariant) — a sibling `inputProps` would collide with
              // `InputProps` under the case-insensitive jsx-no-duplicate-props rule.
              inputProps: {
                ...params.inputProps,
                maxLength,
                'data-cy': 'companyName'
              }
            }}
          />
        )}
      />
      <div
        className={classes.helperZone}
        aria-live="polite"
      >
        {renderHelper()}
      </div>
    </div>
  );
});

export default CompanyAutocomplete;
